import React from 'react';
import classNames from 'classnames';

import Field from '../../Field';

import css from './SectionContainer.module.css';
import AnchorBasedContentTOC from '../../../../components/AnchorBasedContentTOC/AnchorBasedContentTOC';
import { InformationForBuyerData, InformationForSellerData, privacyPolicyData, termNconditionData } from '../../../../components/AnchorBasedContentTOC/ServicePageContent';
import ShopByGenreCarousel from '../../../../components/ShopByGenreCarousel/ShopByGenreCarousel';
import ContactUsPage from '../../../ContactUsPage/ContactUsPage';
import CollectiblesAndAccessoriesContainer from '../../../LandingPage/CollectiblesAndAccessoriesContainer';
import LiveBidsCarouselContainer from '../../../LandingPage/LiveBidsCarouselContainer';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This component can be used to wrap some common styles and features of Section-level components.
 * E.g: const SectionHero = props => (<SectionContainer><H1>Hello World!</H1></SectionContainer>);
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.id id of the section
 * @param {string?} props.as tag/element name. Defaults to 'section'.
 * @param {ReactNode} props.children
 * @param {Object} props.appearance
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} containing wrapper that can be used inside Block components.
 */
const SectionContainer = props => {
  const { className, rootClassName, id, as, children, appearance, options, genreBlocks, ...otherProps } = props;
  const Tag = as || 'section';
  const classes = classNames(rootClassName || css.root, className);
  // if (id === 'home-shop-by-genre' && Array.isArray(genreBlocks) && genreBlocks.length > 0) {
  //   console.log(genreBlocks, '%%%%%%%%%==> genreBlocks');
  // }

  return (
    <Tag className={classes} id={id} {...otherProps}>
      {appearance?.fieldType === 'customAppearance' ? (
        <Field
          data={{ alt: `Background image for ${id}`, ...appearance }}
          className={className}
          options={options}
        />
      ) : null}

      <div className={css.sectionContent}>{children}
        {
          id === 'privacy-policy-page-content' && <AnchorBasedContentTOC
            heroContent={{ heading: 'SectionContainer.privacyPolicy.heading', description: 'SectionContainer.privacyPolicy.description' }}
            ctaSection={{
              img: '/static/images/playing-vincyle.png',
              ctaLabel: 'SectionContainer.getStarted3',
              ctaLink: '/p/contact-us',
              heading: 'SectionContainer.privacyPolicy.cta.heading',
              description: 'SectionContainer.privacyPolicy.cta.description',
              bgColor: '#CB4C4E'
            }}
            serviceContent={privacyPolicyData}
          />
        }
        {
          id === 'information-for-sellers' && <AnchorBasedContentTOC
            heroContent={{
              heading: 'SectionContainer.informationForSellers.heading',
              description: 'SectionContainer.informationForSellers.description',
              btnLabel: 'SectionContainer.informationForBuyer.btnLabel',
              btnLink: '/p/how-it-work'
            }}
            ctaSection={{
              img: '/static/images/checking-vinyle.png',
              ctaLabel: 'SectionContainer.getStarted',
              // ctaLink: '/contact-us',
              heading: 'SectionContainer.informationForSellers.cta.heading',
              description: 'SectionContainer.informationForSellers.cta.description',
              bgColor: '#CB4C4E',
              sellerPage: true

            }}
            serviceContent={InformationForSellerData}
          />
        }

        {
          id === 'information-for-buyer' && <AnchorBasedContentTOC
            heroContent={{
              heading: 'SectionContainer.informationForBuyer.heading',
              description: 'SectionContainer.informationForBuyer.description',
              btnLabel: 'SectionContainer.informationForBuyer.btnLabel',
              btnLink: '/p/how-it-work'
            }}
            ctaSection={{
              img: '/static/images/viewing-vinyle.png',
              ctaLabel: 'SectionContainer.discoverVinyle',
              ctaLink: '/s',
              heading: 'SectionContainer.informationForBuyer.cta.heading',
              description: 'SectionContainer.informationForBuyer.cta.description',
              bgColor: '#008080'
            }}
            serviceContent={InformationForBuyerData}
          />
        }

        {
          id === 'terms-of-service' && <AnchorBasedContentTOC
            heroContent={{
              heading: 'SectionContainer.termsOfService.heading',
              spredDescription: true,
              description: 'SectionContainer.termsOfService.description',
              description2: 'SectionContainer.termsOfService.description2',
              btnLabel: 'SectionContainer.informationForBuyer.btnLabel',
              btnLink: '/p/how-it-work'
            }}
            ctaSection={{
              img: '/static/images/playing-vincyle.png',
              ctaLabel: 'SectionContainer.getStarted2',
              ctaLink: '/',
              heading: 'SectionContainer.termsOfService.cta.heading',
              description: 'SectionContainer.termsOfService.cta.description',
              bgColor: '#FF9000'
            }}
            serviceContent={termNconditionData}
          />
        }
        {id === 'home-shop-by-genre' && <ShopByGenreCarousel genreBlocks={genreBlocks} />}

        {id === 'contact-us-main-container' && <ContactUsPage />}

        {
          id === 'make-bid' && <LiveBidsCarouselContainer />
        }

        {
          id === 'collectibles-and-accessories' && <CollectiblesAndAccessoriesContainer />
        }

      </div>
    </Tag>
  );
};

export default SectionContainer;
